import { useLazyQuery, useQuery } from "@apollo/client";
import equal from "deep-equal";
import { ComponentProps, useCallback, useEffect, useState } from "react";

import Articles from "@components/explore/Articles";
import Events from "@components/explore/Events";
import ExploreHeader from "@components/explore/ExploreHeader";
import ExploreFilters from "@components/forms/ExploreFilters";
import HandleQuery from "@components/HandleQuery";
import ExploreArticlesLoader from "@components/loaders/ExploreArticlesLoader";
import SkeletonLoader from "@components/loaders/SkeletonLoader";
import useSubscribeToAllArticles from "@hooks/subscriptions/useSubscribeToAllArticles";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import useScrollPosition from "@hooks/useScrollPosition";
import useSearchArticles from "@hooks/useSearchArticles";
import { EventTypeEnum, ExploreSectionsEnum, ScrollPositionPageTypeEnum } from "@typing/Enums";
import {
  ArticleContentTypeEnum,
  ArticleSortByEnum,
  GetArticlesBasicDocument,
  GetArticlesBasicQuery,
  GetArticlesBasicQueryVariables,
  GetJourneyWithEmployerBenefitCategoriesDocument,
  GetJourneyWithEmployerBenefitCategoriesQuery,
  GetJourneyWithEmployerBenefitCategoriesQueryVariables,
  GetRecommendationsDocument,
  GetRecommendationsQuery,
  GetRecommendationsQueryVariables,
  GetRecommendedArticlesDocument,
  GetRecommendedArticlesQuery,
  GetRecommendedArticlesQueryVariables,
  RecommendationResourceTypeEnum,
  SortDirectionEnum
} from "@typing/Generated";

type Filters = ComponentProps<typeof ExploreFilters>["filters"];

type Props = {
  journeyId: string;
  section: ExploreSectionsEnum;
};

const Explore = ({ journeyId, section }: Props) => {
  const [filters, setFilters] = useState<Filters>({
    articleContentType: undefined,
    eventType: EventTypeEnum.UPCOMING,
    search: ""
  });

  const journeyQuery = useQuery<
    GetJourneyWithEmployerBenefitCategoriesQuery,
    GetJourneyWithEmployerBenefitCategoriesQueryVariables
  >(GetJourneyWithEmployerBenefitCategoriesDocument, {
    variables: {
      id: journeyId
    }
  });

  useScrollPosition(ScrollPositionPageTypeEnum.LEARN);
  const searchArticles = useSearchArticles(journeyId);

  const [getArticles, getArticlesQuery] = useLazyQuery<GetArticlesBasicQuery, GetArticlesBasicQueryVariables>(
    GetArticlesBasicDocument
  );

  const getContentTypes = useCallback(
    (articleContentType?: ArticleContentTypeEnum) => {
      if (section === ExploreSectionsEnum.EVENTS) {
        return [ArticleContentTypeEnum.WEBINAR];
      }
      if (articleContentType === ArticleContentTypeEnum.TEXT) {
        return [ArticleContentTypeEnum.TEXT];
      } else if (articleContentType === ArticleContentTypeEnum.WEBINAR) {
        return [ArticleContentTypeEnum.WEBINAR];
      } else if (articleContentType === ArticleContentTypeEnum.VIDEO) {
        return [ArticleContentTypeEnum.VIDEO, ArticleContentTypeEnum.MY_STORY];
      }
      return [
        ArticleContentTypeEnum.MY_STORY,
        ArticleContentTypeEnum.TEXT,
        ArticleContentTypeEnum.VIDEO,
        ArticleContentTypeEnum.WEBINAR
      ];
    },
    [section]
  );

  useEffect(() => {
    getArticles({
      variables: {
        contentTypes: getContentTypes(filters.articleContentType),
        count: 40,
        sortBy: ArticleSortByEnum.INTERLEAVING_FOCUS_AREAS_AND_WEBINAR,
        sortDirection: SortDirectionEnum.DESCENDING
      }
    });
  }, [getArticles, getContentTypes, filters.eventType, section, filters.articleContentType]);

  useEffect(() => {
    if (
      section === ExploreSectionsEnum.EVENTS &&
      !getArticlesQuery.loading &&
      getArticlesQuery.data?.articles.pageInfo.hasNextPage
    ) {
      getArticlesQuery.fetchMore({ variables: { cursor: getArticlesQuery.data?.articles.pageInfo.endCursor ?? "" } });
    }
  }, [getArticlesQuery, section]);

  useSubscribeToAllArticles(() => {
    getArticlesQuery.refetch();
  });

  const allArticles = useNodesFromConnection(getArticlesQuery.data?.articles);

  const handleFilterChange = (newFilters: Filters) => {
    if (!equal(newFilters, filters)) {
      setFilters(newFilters);
    }
  };

  const [getRecommendedArticles, getRecommendedArticlesQuery] = useLazyQuery<
    GetRecommendedArticlesQuery,
    GetRecommendedArticlesQueryVariables
  >(GetRecommendedArticlesDocument);

  const getRecommendationsQuery = useQuery<GetRecommendationsQuery, GetRecommendationsQueryVariables>(
    GetRecommendationsDocument,
    {
      variables: {
        journeyId,
        resourceType: RecommendationResourceTypeEnum.ARTICLE
      }
    }
  );

  useEffect(() => {
    getRecommendedArticles({
      variables: {
        contentTypes: getContentTypes(filters.articleContentType),
        count: 15,
        journeyId,
        prioritizeExpertRecommended: true,
        prioritizeGoalRecommended: true
      }
    });
  }, [filters.articleContentType, filters.search, getContentTypes, getRecommendedArticles, journeyId, section]);

  const queries = [getArticlesQuery, getRecommendedArticlesQuery, getRecommendationsQuery];

  const fetchMoreArticles = () => {
    if (getArticlesQuery.data?.articles?.pageInfo?.hasNextPage) {
      getArticlesQuery.fetchMore({
        variables: {
          cursor: getArticlesQuery.data?.articles?.pageInfo?.endCursor
        }
      });
    }
  };

  return (
    <div>
      <ExploreHeader exploreSection={section} journeyId={journeyId} />
      <ExploreFilters
        categoriesToInclude={journeyQuery.data?.journey.clientEmployerBenefitCategories ?? []}
        exploreSection={section}
        filters={filters}
        onChange={handleFilterChange}
        onSearch={searchArticles}
      />

      <SkeletonLoader loadingContent={<ExploreArticlesLoader />} queries={queries}>
        <HandleQuery query={getRecommendedArticlesQuery}>
          {recommendedData => (
            <>
              {section === ExploreSectionsEnum.EVENTS ||
              (section === ExploreSectionsEnum.ARTICLES &&
                filters.articleContentType === ArticleContentTypeEnum.WEBINAR) ? (
                <Events
                  eventType={section === ExploreSectionsEnum.EVENTS ? filters.eventType : undefined}
                  journeyId={journeyId}
                  webinars={allArticles}
                />
              ) : (
                <Articles
                  allArticles={allArticles}
                  exploreSection={section}
                  fetchMore={fetchMoreArticles}
                  journeyId={journeyId}
                  recommendedArticles={recommendedData.recommendedArticles}
                />
              )}
            </>
          )}
        </HandleQuery>
      </SkeletonLoader>
    </div>
  );
};

export default Explore;
